// Ortak personel düzenleme modeli (#29) — saf TS. Hedef satır, T.C. kilidi ve değişiklik tipi;
// wire gövdesini her portalın kendi `PersonnelClient`ı kurar (api-contract §13.3 / §13.6).
// Sunucunun maskelediği değer yalnız gösterimdir: formda tutulmaz, PUT'a girmez.
import type { ContractorAccessLevel, ContractorPersonnelDto } from '../api/contractors';
import { isServerMaskedTc } from './tcKimlik';

export interface PersonnelEditTarget {
  uuid: string;
  firstName: string;
  lastName: string;
  accessLevel: ContractorAccessLevel;
  /** Ham wire değeri — `status`'ten türetilmez. */
  isActive: boolean;
  /** Düz değer; `personalDataMasked` ise sunucunun maskeli metni. */
  tcKimlikNo: string | null;
  /** Sunucu bayrağı, YA DA T.C. düz 11 hane değil (`isServerMaskedTc` — fail closed). */
  personalDataMasked: boolean;
  email: string | null;
  phone: string | null;
}

/** `kind === 'Personnel'` ve `isActive` boolean değilse düzenleme sunulmaz (fail closed). */
export function toEditTarget(row: ContractorPersonnelDto): PersonnelEditTarget | null {
  if (row.kind !== 'Personnel' || typeof row.isActive !== 'boolean') {
    return null;
  }
  return {
    uuid: row.uuid,
    firstName: row.firstName,
    lastName: row.lastName,
    accessLevel: row.accessLevel,
    isActive: row.isActive,
    tcKimlikNo: row.tcKimlikNo,
    personalDataMasked: row.personalDataMasked || isServerMaskedTc(row.tcKimlikNo),
    email: row.email ?? null,
    phone: row.phone ?? null,
  };
}

/**
 * Kayıtlı ad doluysa alan zorunludur (boşaltılamaz). Kayıtlı ad boşsa (ör. adsız davetle aktive
 * olmuş kişi) alan boş bırakılabilir ve PUT'a `null` (= değişmez) gider — ana firma, kimliğini
 * göremediği birine ad uydurmadan pasife alabilir/erişimini değiştirebilir (#29 I-1).
 */
export function isNameRequired(stored: string): boolean {
  return stored.trim().length > 0;
}

export type TcLock = 'none' | 'setOnce' | 'noPermission';

export function tcLock(t: PersonnelEditTarget): TcLock {
  if (t.personalDataMasked || isServerMaskedTc(t.tcKimlikNo)) return 'noPermission';
  return (t.tcKimlikNo ?? '').trim().length > 0 ? 'setOnce' : 'none';
}

/**
 * `keep` hiçbir değer taşımaz: iki portal da `tcKimlikNo` anahtarını yalnız `set`te koyar
 * (anahtar yok = değişmez) → maskeli ya da bayat bir T.C. ne geri gönderilebilir ne temizlenebilir.
 */
export type PersonnelTcChange = { kind: 'keep' } | { kind: 'set'; value: string };

export interface PersonnelChange {
  /** `null` = değişmez — yalnız kayıtlı ad boşken ve alan boş bırakıldığında; asla `""` değil. */
  firstName: string | null;
  lastName: string | null;
  accessLevel: ContractorAccessLevel;
  isActive: boolean;
  tc: PersonnelTcChange;
}

export function tcChange(t: PersonnelEditTarget, typed: string): PersonnelTcChange {
  if (tcLock(t) !== 'none') return { kind: 'keep' };
  const v = typed.trim();
  return v.length > 0 ? { kind: 'set', value: v } : { kind: 'keep' };
}

export interface PersonnelClient {
  /** Portalın wire gövdesini kurar ve kendi PUT'unu çağırır. */
  save: (userUuid: string, change: PersonnelChange) => Promise<ContractorPersonnelDto>;
  /** Başarıdan sonra: yanıtı kayıt listesi önbelleğine yaz, bağlı tüm sorguları geçersiz kıl. */
  afterSave: (updated: ContractorPersonnelDto) => void;
  /**
   * Her başarısız kaydetmeden sonra (`VehicleFormSheet` emsali): satır bayat olabilir — 404, yetki
   * değişimi sonrası 403 `PERSONNEL_TC_WRITE_FORBIDDEN`, 409 `PERSONNEL_TC_ALREADY_SET` — liste
   * sorgularını geçersiz kıl. Oturumdan bağımsızdır (terk edilmiş kaydetmede de çağrılır).
   */
  afterError?: (error: unknown) => void;
}
